<template>
  <div class="ed-tabela">
    <div class="ed-tabela__bulkbar" v-if="!disableCheckAll && checkItem.length > 0">
      <span class="ed-tabela__bulkbar-count">{{ checkItem.length }} selecionado(s)</span>

      <span class="ed-tabela__bulkbar-divider"></span>
      <span class="ed-tabela__bulkbar-info">{{ selectedEligibleForXml.length }} pode(m) gerar XML</span>

      <template v-if="onBulkGerarXml">
        <span class="ed-tabela__bulkbar-divider"></span>
        <button
          class="ed-tabela__bulkbar-action"
          :disabled="!selectedEligibleForXml.length"
          @click="handleBulkGerarXml"
        >
          <i class="fa-solid fa-file-circle-plus"></i> Gerar XML
        </button>
      </template>

      <template v-if="onEditAll">
        <span class="ed-tabela__bulkbar-divider"></span>
        <button class="ed-tabela__bulkbar-action" @click="handleBulkEditAll">
          <i class="fa fa-pencil-square-o"></i> Editar selecionados
        </button>
      </template>

      <template v-if="onDelete">
        <span class="ed-tabela__bulkbar-divider"></span>
        <button class="ed-tabela__bulkbar-action ed-tabela__bulkbar-action--danger" @click="handleBulkDelete">
          <i class="fa fa-trash"></i> Excluir selecionados
        </button>
      </template>

      <span class="ed-tabela__bulkbar-divider"></span>
      <button class="ed-tabela__bulkbar-action" @click="clearSelection">Cancelar</button>
    </div>

    <div class="ed-tabela__scroll">
      <table class="ed-tabela__table">
        <thead>
          <tr>
            <th class="ed-tabela__th-check" v-if="!disableCheckAll">
              <input type="checkbox" v-model="checkAll" @change="handleSelectAll" />
            </th>
            <th
              v-for="col in tableHead"
              :key="col.key"
              :style="{ width: col.width }"
              :class="{ 'is-sortable': col.sortable }"
              @click="col.sortable && toggleSort(col.key)"
            >
              {{ col.label }}
              <i v-if="col.sortable && sortKey == col.key && !sortDesc" class="fa fa-sort-asc"></i>
              <i v-if="col.sortable && sortKey == col.key && sortDesc" class="fa fa-sort-desc"></i>
              <i v-if="col.sortable && sortKey != col.key" class="fa fa-sort ed-tabela__sort-idle"></i>
              <i
                v-if="col.info"
                class="fa fa-question-circle ed-tabela__th-info"
                @click.stop
                @mouseenter="showTooltip(col.info, $event)"
                @mouseleave="hideTooltip"
              ></i>
            </th>
            <th class="ed-tabela__th-actions">&nbsp;</th>
          </tr>
        </thead>

        <tbody v-if="loadingTable">
          <tr>
            <td :colspan="colspan" class="ed-tabela__state">
              <i class="fa fa-spinner fa-spin"></i> Carregando...
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="!sortedData.length">
          <tr>
            <td :colspan="colspan" class="ed-tabela__state">{{ emptyMessage }}</td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr v-for="row in sortedData" :key="row._data.id" :class="{ 'is-deleted': row._data.deleted_at }">
            <td class="ed-tabela__th-check" v-if="!disableCheckAll">
              <input
                type="checkbox"
                :value="row._data.id"
                v-model="checkItem"
                :disabled="row.options && row.options.disable_select"
                :title="row.options && row.options.disable_select ? 'Este registro não pode ser selecionado' : null"
                @change="handleSelectItem"
              />
            </td>

            <td v-for="col in tableHead" :key="col.key">
              <!-- EMPRESA -->
              <template v-if="col.show_empresa_link">
                <a :href="getUrlEmpresa(row._data)" target="_blank" class="ed-tabela__link">
                  <i class="fa fa-external-link" aria-hidden="true"></i> <span v-html="row[col.key]"></span>
                </a>
                <div v-if="col.show_fdt && row._data.fdt && row._data.fdt.pcmso" class="ed-tabela__sub">
                  {{ row._data.fdt.pcmso }} ({{ row._data.fdt.descricao }})
                </div>
              </template>

              <!-- FUNCIONARIO -->
              <template v-else-if="col.show_funcionario_link">
                <a :href="getUrlFuncionario(row._data)" target="_blank" class="ed-tabela__link">
                  <i class="fa fa-external-link" aria-hidden="true"></i> <span v-html="row[col.key]"></span>
                </a>
                <div v-if="col.show_funcao && row._data.funcao" class="ed-tabela__sub">
                  {{ row._data.funcao.nome }}
                </div>
              </template>

              <!-- CONSULTA -->
              <template v-else-if="col.show_consulta_link && row._data.id_con">
                <a :href="getUrlConsulta(row._data)" target="_blank" class="ed-tabela__link">
                  <i class="fa fa-external-link" aria-hidden="true"></i> <span v-html="row[col.key]"></span>
                </a>
              </template>

              <!-- LOTE -->
              <template v-else-if="col.show_lote_link">
                <a :href="getUrlLote(row._data)" target="_blank" class="ed-tabela__link">
                  <span v-html="row[col.key]"></span>
                </a>
              </template>

              <!-- STATUS -->
              <template v-else-if="col.key == 'tipo_status' && row._data.tipo_status">
                <button
                  class="ed-tabela__badge"
                  :class="{ 'ed-tabela__badge--conflito': hasConflito(row._data), 'ed-tabela__badge--timeline': col.show_status_timeline }"
                  :style="{
                    color: row._data.tipo_status.cor,
                    background: hexToRgba(row._data.tipo_status.cor, 0.12),
                  }"
                  :disabled="!hasConflito(row._data) && !col.show_status_timeline"
                  :title="hasConflito(row._data) ? conflitoCount(row._data) + ' conflito(s) encontrado(s)' : null"
                  @click="handleStatusClick(col, row._data, $event)"
                >
                  <i
                    v-if="row._data.tipo_status.icon"
                    :class="[row._data.tipo_status.icon, { 'ed-tabela__badge-pulse': hasConflito(row._data) }]"
                  ></i>
                  {{ row._data.tipo_status.descricao }}
                  <i v-if="col.show_status_timeline" class="fa fa-chevron-down ed-tabela__badge-caret"></i>
                </button>
              </template>

              <!-- USER -->
              <template v-else-if="col.show_user">
                <span v-html="row[col.key]"></span>
                <div v-if="row._data.usuario" class="ed-tabela__sub">{{ row._data.usuario.usuario }}</div>
              </template>

              <!-- RECIBO (com indicação de edição manual) -->
              <template v-else-if="col.show_recibo_manual">
                <button
                  v-if="row._data.usuario_recibo_manual"
                  class="ed-tabela__recibo-manual-icon"
                  @click="toggleReciboManual(row._data, $event)"
                >
                  <i class="fa fa-user"></i>
                </button>
                <span v-html="row[col.key]"></span>
              </template>

              <!-- ARQUIVO -->
              <template v-else-if="col.show_arquivo">
                <Download :arquivo="row[col.key]" v-if="row[col.key] && row[col.key].id" />
                <span v-else>---</span>
              </template>

              <!-- EVENTOS -->
              <template v-else-if="col.show_eventos && row[col.key]">
                <button class="ed-tabela__eventos-btn" @click="toggleEventos(row[col.key], $event)">
                  <span class="ed-tabela__eventos-total">{{ row[col.key].total }}</span> evento(s)
                  <span class="ed-tabela__eventos-count ed-tabela__eventos-count--success">
                    {{ row[col.key].enviado || 0 }}
                  </span>
                  <span class="ed-tabela__eventos-sep">/</span>
                  <span class="ed-tabela__eventos-count ed-tabela__eventos-count--danger">
                    {{ row[col.key].erro || 0 }}
                  </span>
                </button>
              </template>

              <!-- INLINE EDIT -->
              <template v-else-if="col.edit && editableRows">
                <input
                  v-if="col.edit == 'text'"
                  type="text"
                  class="ed-tabela__input"
                  v-model="row._data[col.key]"
                />
                <input
                  v-else-if="col.edit == 'money'"
                  type="text"
                  class="ed-tabela__input"
                  v-model="row._data[col.key]"
                  v-mask="'money'"
                />
                <input
                  v-else-if="col.edit == 'number'"
                  type="number"
                  class="ed-tabela__input"
                  v-model="row._data[col.key]"
                />
              </template>

              <!-- DEFAULT -->
              <template v-else><span :class="{ 'ed-tabela__value--bold': col.bold }" v-html="row[col.key]"></span></template>
            </td>

            <td class="ed-tabela__actions">
              <button
                v-for="(btn, i) in row.buttons"
                :key="i"
                class="ed-tabela__icon-btn"
                :style="btnStyle(btn)"
                :aria-label="btn.title"
                :disabled="!!btn.disabled"
                @click="btn.click && btn.click(row._data)"
                @mouseenter="showTooltip(btn.title, $event)"
                @mouseleave="hideTooltip"
              >
                <i :class="btn.icon"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="ed-tabela__footer" v-if="pagination && pagination.total">
      <select class="ed-tabela__pp" v-model.number="ppage" @change="handlePerPage">
        <option v-for="p in lstPages" :key="p" :value="p">{{ p }} por página</option>
      </select>

      <ed-paginate :pagination="pagination" :navigation="handleNavigate" />
    </div>

    <div v-if="popover.open" class="ed-tabela__conflito-overlay" @click="closePopover"></div>

    <div v-if="popover.open" class="ed-tabela__conflito-popover" :style="popover.pos" @click.stop>
      <conflitos-detalhe v-if="popover.type == 'conflito'" :item="popover.payload" />

      <div v-else-if="popover.type == 'eventos'" class="ed-tabela__eventos-detalhe">
        <div class="ed-tabela__eventos-detalhe-row">
          <span>Total</span> <b>{{ popover.payload.total }}</b>
        </div>
        <div class="ed-tabela__eventos-detalhe-row ed-tabela__eventos-detalhe-row--success">
          <i class="fa fa-check-circle"></i> <span>Com sucesso</span> <b>{{ popover.payload.enviado || 0 }}</b>
        </div>
        <div class="ed-tabela__eventos-detalhe-row ed-tabela__eventos-detalhe-row--danger">
          <i class="fa fa-exclamation-triangle"></i> <span>Com erro</span> <b>{{ popover.payload.erro || 0 }}</b>
        </div>
      </div>

      <div v-else-if="popover.type == 'timeline'" class="ed-tabela__timeline">
        <div
          class="ed-tabela__timeline-step"
          :class="{
            'ed-tabela__timeline-step--done': step.ordem &lt; timelineAtual(popover.payload).ordem,
            'ed-tabela__timeline-step--atual': step.ordem == timelineAtual(popover.payload).ordem,
            'ed-tabela__timeline-step--erro': step.erro &amp;&amp; step.ordem == timelineAtual(popover.payload).ordem,
          }"
          v-for="step in timelinePassos(popover.payload)"
          :key="step.id"
        >
          <i class="fa" :class="timelineStepIcon(step, popover.payload)"></i>
          <span>{{ step.descricao }}</span>
        </div>
      </div>

      <div v-else-if="popover.type == 'recibo_manual'" class="ed-tabela__recibo-manual-detalhe">
        <i class="fa fa-user"></i>
        <div>
          Recibo editado manualmente por <b>{{ popover.payload.usuario_recibo_manual.usuario }}</b> em
          {{ toDate(popover.payload.recibo_manual_at, false) }}
        </div>
      </div>
    </div>

    <div
      v-if="tooltip.visible"
      class="ed-tabela__tooltip"
      :class="{ 'ed-tabela__tooltip--below': tooltip.below }"
      :style="{ top: tooltip.top + 'px', left: tooltip.left + 'px' }"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>

<script>
import Rest from "common/rest";
import Fct from "common/fct";
import EdPaginate from "common/paginate";
import Download from "common/download";
import ConflitosDetalhe from "./conflitos-detalhe";

const STATUS_EMAIL = { PENDENTE: 72, ENVIANDO: 73, ENVIADO: 74, ERRO: 75 };

const STATUS_EMAIL_TIMELINE = [
  { id: STATUS_EMAIL.PENDENTE, ordem: 1, descricao: "Pendente" },
  { id: STATUS_EMAIL.ENVIANDO, ordem: 2, descricao: "Enviando" },
  { id: STATUS_EMAIL.ENVIADO, ordem: 3, descricao: "Enviado" },
  { id: STATUS_EMAIL.ERRO, ordem: 3, descricao: "Erro", erro: true },
];

export default {
  name: "esocial-tabela",
  mixins: [Rest, Fct],
  components: { EdPaginate, Download, ConflitosDetalhe },
  props: {
    tableHead: {
      type: Array,
      default: () => [],
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    response: {
      type: Object,
      default: () => ({}),
    },
    loadingTable: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: null,
    },
    onSearch: {
      type: Function,
      default: null,
    },
    onDelete: {
      type: Function,
      default: null,
    },
    onBulkGerarXml: {
      type: Function,
      default: null,
    },
    onEditAll: {
      type: Function,
      default: null,
    },
    disableCheckAll: {
      type: Boolean,
      default: false,
    },
    editableRows: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "Nenhum registro encontrado para os filtros atuais.",
    },
  },
  data() {
    return {
      checkAll: false,
      checkItem: [],
      sortKey: null,
      sortDesc: false,
      ppage: 50,
      lstPages: [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300, 400, 500],
      popover: { open: false, type: null, payload: null, pos: {} },
      tooltip: { visible: false, text: "", top: 0, left: 0, below: false },
    };
  },
  mounted() {
    window.addEventListener("scroll", this.closePopover, true);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.closePopover, true);
  },
  watch: {
    tableData() {
      this.checkAll = false;
      this.checkItem = [];
      this.closePopover();
    },
    pagination() {
      this.ppage = this.pagination && this.pagination.per_page ? parseInt(this.pagination.per_page) : 50;
    },
  },
  computed: {
    colspan() {
      return this.tableHead.length + (this.disableCheckAll ? 1 : 2);
    },
    selectedEligibleForXml() {
      return this.tableData.filter(
        (row) =>
          this.checkItem.indexOf(row._data.id) != -1 &&
          row.buttons &&
          row.buttons.some((btn) => btn.title == "Gerar arquivo XML")
      );
    },
    emptyMessage() {
      if (this.response && this.response.status && this.response.status != 200) {
        return this.response.status + (this.response.msg ? " - " + this.response.msg : "");
      }
      return this.emptyText;
    },
    sortedData() {
      if (!this.sortKey) return this.tableData;

      var key = this.sortKey;
      var desc = this.sortDesc;

      return [...this.tableData].sort((a, b) => {
        var valA = (a[key] == null ? "" : a[key]).toString();
        var valB = (b[key] == null ? "" : b[key]).toString();
        var result = valA.localeCompare(valB, "pt-BR", { numeric: true });
        return desc ? -result : result;
      });
    },
  },
  methods: {
    toggleSort(key) {
      if (this.sortKey != key) {
        this.sortKey = key;
        this.sortDesc = false;
      } else if (!this.sortDesc) {
        this.sortDesc = true;
      } else {
        this.sortKey = null;
        this.sortDesc = false;
      }
    },

    hasConflito(data) {
      return !!(data && ((data.log_erro && data.log_erro.length) || (data.log_warning && data.log_warning.length)));
    },

    handleStatusClick(col, data, event) {
      if (col.show_status_timeline) {
        this.togglePopover("timeline", data, data.id, event);
      } else if (this.hasConflito(data)) {
        this.toggleConflito(data, event);
      }
    },

    timelinePassos(data) {
      var erro = data.tipo_status && data.tipo_status.id == STATUS_EMAIL.ERRO;
      return STATUS_EMAIL_TIMELINE.filter((step) => !step.erro || erro);
    },

    timelineAtual(data) {
      var atualId = data.tipo_status ? data.tipo_status.id : null;
      return STATUS_EMAIL_TIMELINE.find((step) => step.id == atualId) || STATUS_EMAIL_TIMELINE[0];
    },

    timelineStepIcon(step, data) {
      var atual = this.timelineAtual(data);

      if (step.ordem < atual.ordem) return "fa-check-circle";
      if (step.ordem == atual.ordem) return step.erro ? "fa-times-circle" : "fa-dot-circle-o";
      return "fa-circle-o";
    },

    conflitoCount(data) {
      return (data.log_erro ? data.log_erro.length : 0) + (data.log_warning ? data.log_warning.length : 0);
    },

    toggleConflito(data, event) {
      this.togglePopover("conflito", data, data.id, event);
    },

    toggleEventos(data, event) {
      this.togglePopover("eventos", data, event.currentTarget, event);
    },

    toggleReciboManual(data, event) {
      this.togglePopover("recibo_manual", data, data.id, event);
    },

    togglePopover(type, payload, identity, event) {
      if (this.popover.open && this.popover.type == type && this.popover._identity == identity) {
        this.closePopover();
        return;
      }

      var rect = event.currentTarget.getBoundingClientRect();
      var estimatedHeight = 300;
      var left = Math.max(8, Math.min(rect.left, window.innerWidth - 340));
      var pos;

      if (window.innerHeight - rect.bottom < estimatedHeight) {
        pos = { left: left + "px", bottom: window.innerHeight - rect.top + 6 + "px", top: "auto" };
      } else {
        pos = { left: left + "px", top: rect.bottom + 6 + "px", bottom: "auto" };
      }

      this.popover = { open: true, type: type, payload: payload, pos: pos, _identity: identity };
    },

    closePopover() {
      if (this.popover.open) this.popover = { open: false, type: null, payload: null, pos: {} };
      this.hideTooltip();
    },

    showTooltip(text, event) {
      if (!text) return;

      var rect = event.currentTarget.getBoundingClientRect();
      var below = rect.top < 60;

      this.tooltip = {
        visible: true,
        text: text,
        below: below,
        top: below ? rect.bottom + 8 : rect.top - 8,
        left: Math.max(70, Math.min(rect.left + rect.width / 2, window.innerWidth - 70)),
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    clearSelection() {
      this.checkAll = false;
      this.checkItem = [];
    },

    handleSelectAll() {
      this.checkItem = this.checkAll
        ? this.tableData.filter((row) => !(row.options && row.options.disable_select)).map((row) => row._data.id)
        : [];
    },

    handleSelectItem() {
      this.checkAll = this.checkItem.length > 0 && this.checkItem.length == this.tableData.length;
    },

    handleBulkDelete() {
      if (this.checkItem.length && this.onDelete) {
        this.onDelete(this.checkItem);
        this.clearSelection();
      }
    },

    handleBulkGerarXml() {
      var ids = this.selectedEligibleForXml.map((row) => row._data.id);

      if (ids.length && this.onBulkGerarXml) {
        this.onBulkGerarXml(ids);
        this.clearSelection();
      }
    },

    handleBulkEditAll() {
      var data = this.tableData.filter((row) => this.checkItem.indexOf(row._data.id) != -1).map((row) => row._data);

      if (data.length && this.onEditAll) {
        this.onEditAll(data);
        this.clearSelection();
      }
    },

    handleNavigate(page) {
      if (this.onSearch) this.onSearch(this.search, page, this.ppage);
    },

    handlePerPage() {
      if (this.onSearch) this.onSearch(this.search, 1, this.ppage);
    },

    getUrlFuncionario(_data) {
      if (this.$root.$session.sub_domain == "empresa") {
        return (
          "https://" +
          process.env.URL_API.empresa +
          "/" +
          this.$root.$session.cliente.tenant +
          "/funcionarios/editar/" +
          (_data.funcionario ? _data.funcionario.id : _data.id)
        );
      }
      return this.env.url_cs + "/app/funcionarios_info.php?abre=" + (_data.funcionario ? _data.funcionario.id : _data.id);
    },

    getUrlEmpresa(_data) {
      if (this.$root.$session.sub_domain == "empresa") {
        return "https://" + process.env.URL_API.empresa + "/" + this.$root.$session.cliente.tenant + "/meu-cadastro?";
      }
      return this.env.url_cs + "/app/empresas_info.php?abre=" + (_data.empresa ? _data.empresa.id : _data.empresa_id);
    },

    getUrlConsulta(_data) {
      if (this.$root.$session.sub_domain == "empresa") return "#";
      return this.env.url_cs + "/app/consulta.php?con=" + _data.id_con;
    },

    btnStyle(btn) {
      if (btn.style) return btn.style;

      var colors = { warning: "#a8791f", success: "#34c142", primary: "#09f", danger: "#c0392b", info: "#568cdd" };

      return btn.color && colors[btn.color] ? "color:" + colors[btn.color] : "";
    },

    hexToRgba(hex, alpha) {
      if (!hex) return "transparent";

      var h = hex.replace("#", "");
      if (h.length == 3) h = h.split("").map((c) => c + c).join("");

      var r = parseInt(h.substring(0, 2), 16);
      var g = parseInt(h.substring(2, 4), 16);
      var b = parseInt(h.substring(4, 6), 16);

      return "rgba(" + r + "," + g + "," + b + "," + alpha + ")";
    },

    getUrlLote(_data) {
      if (this.$root.$session.sub_domain == "empresa") {
        return (
          "https://" +
          process.env.URL_API.empresa +
          "/" +
          this.$root.$session.cliente.tenant +
          "/esocial/gerar-lote" +
          (_data.lote ? "/" + _data.lote : "")
        );
      }
      return "https://" + process.env.URL_API.web + "/esocial/lotes" + (_data.lote ? "/" + _data.lote : "");
    },
  },
};
</script>

<style lang="scss" scoped>
.ed-tabela {
  --muted: #6b7280;
  --ink: #1f2329;
  --line: #eef0f2;
  --accent: #09f;

  &__bulkbar {
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    z-index: 2200;
    display: flex;
    align-items: center;
    gap: 14px;
    background: #14161c;
    border-radius: 12px;
    padding: 10px 16px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
    font-size: 13px;
    color: #fff;
    white-space: nowrap;
  }

  &__bulkbar-count {
    font-weight: 700;
  }

  &__bulkbar-info {
    color: #9aa4b2;
  }

  &__bulkbar-divider {
    width: 1px;
    height: 18px;
    background: rgba(255, 255, 255, 0.16);
  }

  &__bulkbar-action {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 30px;
    padding: 0 10px;
    border: none;
    background: transparent;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    cursor: pointer;
    font-family: inherit;

    i {
      font-size: 12px;
      color: var(--accent);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--danger i {
      color: #ff6b6b;
    }
  }

  &__scroll {
    overflow-x: auto;
    border: 1px solid var(--line);
    border-radius: 8px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11.5px;

    thead th {
      background: #f0f1f3;
      border-bottom: 2px solid var(--line);
      color: var(--muted);
      text-transform: uppercase;
      font-weight: 700;
      font-size: 10px;
      letter-spacing: 0.03em;
      padding: 9px 10px;
      text-align: left;
      white-space: nowrap;
      user-select: none;

      &.is-sortable {
        cursor: pointer;

        &:hover {
          color: var(--ink);

          .ed-tabela__sort-idle {
            opacity: 0.5;
          }
        }
      }

      i {
        margin-left: 4px;
        font-size: 9px;
      }
    }

    .ed-tabela__th-info {
      cursor: help;
      font-size: 10px;
    }

    .ed-tabela__sort-idle {
      opacity: 0;
      transition: opacity 0.12s;
    }

    .fa-sort-asc,
    .fa-sort-desc {
      color: var(--accent);
    }

    tbody tr {
      border-bottom: 1px solid var(--line);

      &:hover {
        background: #f7f9fb;
      }

      &.is-deleted {
        color: #c0392b;
      }
    }

    td {
      padding: 6px 10px;
      vertical-align: middle;
    }
  }

  &__th-check {
    width: 34px;
    text-align: center;
  }

  &__th-actions {
    width: 120px;
  }

  &__state {
    text-align: center;
    padding: 28px 10px;
    color: #9aa0a8;
    font-size: 12px;
  }

  &__link {
    color: var(--ink);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: var(--accent);
    }
  }

  &__sub {
    font-size: 10px;
    color: var(--muted);
    margin-top: 1px;
    line-height: 1.3;
  }

  &__value--bold {
    font-weight: 700;
  }

  &__eventos-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: transparent;
    padding: 0;
    font-size: inherit;
    font-family: inherit;
    color: var(--ink);
    cursor: pointer;

    &:hover {
      color: var(--accent);
    }
  }

  &__eventos-total {
    font-weight: 700;
  }

  &__eventos-sep {
    color: var(--muted);
  }

  &__eventos-count {
    font-weight: 700;

    &--success {
      color: #1a8a3e;
    }

    &--danger {
      color: #c0392b;
    }
  }

  &__eventos-detalhe-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 4px 0;

    b {
      margin-left: auto;
    }

    &--success {
      color: #1a8a3e;
    }

    &--danger {
      color: #c0392b;
    }
  }

  &__recibo-manual-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-right: 5px;
    border: none;
    background: none;
    padding: 0;
    color: #1a8a3e;
    cursor: pointer;
    vertical-align: middle;

    &:hover {
      color: #146b2e;
    }

    i {
      font-size: 11px;
    }
  }

  &__recibo-manual-detalhe {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: var(--ink);

    i {
      color: #1a8a3e;
      font-size: 13px;
      margin-top: 1px;
    }
  }

  &__timeline {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__timeline-step {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #c7ccd3;

    i {
      font-size: 13px;
    }

    &--done {
      color: #9aa0a8;

      i {
        color: #1a8a3e;
      }
    }

    &--atual {
      color: var(--ink);
      font-weight: 700;

      i {
        color: var(--accent);
      }
    }

    &--erro {
      color: #c0392b;

      i {
        color: #c0392b;
      }
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border: none;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    font-family: inherit;
    letter-spacing: 0.01em;
    white-space: nowrap;
    cursor: default;

    i {
      font-size: 9px;
    }

    &--conflito {
      cursor: pointer;

      &:hover {
        filter: brightness(0.94);
      }
    }

    &--timeline {
      cursor: pointer;

      &:hover {
        filter: brightness(0.94);
      }
    }
  }

  &__badge-caret {
    font-size: 8px !important;
    opacity: 0.7;
  }

  &__badge-pulse {
    animation: ed-tabela-pulse 1.6s ease-in-out infinite;
  }

  &__conflito-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
  }

  &__conflito-popover {
    position: fixed;
    z-index: 2001;
    width: 320px;
    max-height: 360px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
    padding: 14px;
  }

  &__input {
    width: 100%;
    height: 28px;
    border: 1px solid #d0d4d9;
    border-radius: 6px;
    padding: 0 8px;
    font-size: 11px;

    &:focus {
      outline: none;
      border-color: var(--accent);
    }
  }

  &__actions {
    text-align: center;
    white-space: nowrap;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 0 1px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--muted);
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;

    &:hover {
      background: #fff;
      border-color: #e4e7ed;
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__tooltip {
    position: fixed;
    transform: translate(-50%, -100%);
    z-index: 3000;
    max-width: 300px;
    background: var(--ink);
    color: #fff;
    font-size: 10px;
    font-weight: 500;
    font-family: inherit;
    line-height: 1.4;
    text-transform: none;
    letter-spacing: 0;
    white-space: normal;
    text-align: center;
    padding: 6px 9px;
    border-radius: 6px;
    pointer-events: none;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.2);

    &--below {
      transform: translate(-50%, 0);
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding-top: 10px;
  }

  &__pp {
    height: 30px;
    border: 1px solid #d0d4d9;
    border-radius: 6px;
    padding: 0 8px;
    font-size: 12px;
    color: #374151;
    background: #fff;
  }

  input[type="checkbox"] {
    -webkit-appearance: none;
    appearance: none;
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    flex-shrink: 0;
    vertical-align: middle;
    width: 15px;
    height: 15px;
    margin: 0;
    padding: 0;
    border: 1px solid #c7ccd3;
    border-radius: 3px;
    background: #fff;
    cursor: pointer;
    transition: background-color 0.12s, border-color 0.12s;

    &:hover {
      border-color: var(--accent);
    }

    &:checked {
      background: var(--accent);
      border-color: var(--accent);

      &::after {
        content: "";
        position: absolute;
        left: 4px;
        top: 0px;
        width: 4px;
        height: 7px;
        border: solid #fff;
        border-width: 0 1.5px 1.5px 0;
        transform: rotate(45deg);
      }
    }

    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      background: #f1f2f4;
      border-color: #e4e7ed;
    }
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  select:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
}

@keyframes ed-tabela-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
</style>



// WEBPACK FOOTER //
// tabela.vue?066292ea